import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { surveyService, providerService } from '../../../services/surveyService';
import api from '../../../services/api';
import {
  Button, StatusBadge, Card, Modal, Spinner,
  EmptyState, PageHeader, Input, Select, PeriodBadges
} from '../../../shared/sharedComponent';
import { toast } from 'react-toastify';
import './SurveyList.css';

const STATUS_OPTIONS = ['', 'draft', 'sent', 'in_progress', 'submitted', 'overdue', 'closed'];
const STATUS_LABELS = { overdue: 'Pending', submitted: 'Completed', sent: 'Submitted' };
const TYPE_OPTIONS   = ['', 'monthly', 'quarterly'];
const SECTOR_OPTIONS = ['', 'manufacturing', 'wholesale_retail'];
const FORM_OPTIONS   = ['', 'MQ1', 'WRTQ1', 'MM1', 'Monthly_WRT', 'GQ1'];
const FORM_LABELS = {
  MQ1: 'MQ1 — Manufacturing Quarterly',
  WRTQ1: 'WRTQ1 — Wholesale & Retail Quarterly',
  MM1: 'MM1 — Manufacturing Monthly',
  Monthly_WRT: 'Monthly WRT — Wholesale & Retail Monthly',
  GQ1: 'GQ1 — Generic Questionnaire',
};

// ── Time ago helper ──────────────────────────────────────────
const timeAgo = (dateStr) => {
  if (!dateStr) return null;
  const diff   = Date.now() - new Date(dateStr).getTime();
  const mins   = Math.floor(diff / 60000);
  const hours  = Math.floor(diff / 3600000);
  const days   = Math.floor(diff / 86400000);
  const weeks  = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7)   return `${days}d ago`;
  if (weeks < 5)  return `${weeks}w ago`;
  return `${months}mo ago`;
};

const statusTime = (s) => {
  if (s.survey_status === 'submitted') return timeAgo(s.survey_date_submitted);
  if (s.survey_status === 'in_progress') return timeAgo(s.updated_at || s.created_at);
  if (s.survey_status === 'overdue')  return timeAgo(s.survey_close_date);
  return timeAgo(s.created_at);
};

const SurveysList = () => {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  // Filters
  const [filters, setFilters] = useState({ status: '', survey_type: '', sector: '', form_code: '', date_from: '', date_to: '' });

  // Dispatch modal
  const [dispatchModal, setDispatchModal]   = useState(false);
  const [allProviders, setAllProviders]     = useState([]);
  const [dispatchEstFilter, setDispatchEstFilter] = useState('');
  const [dispatchSurvey, setDispatchSurvey] = useState(null);
  const [providers, setProviders] = useState([]);
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [dispatching, setDispatching] = useState(false);

  // Dispatch log modal
  const [logModal, setLogModal]     = useState(false);
  const [logSurvey, setLogSurvey]   = useState(null);
  const [logEntries, setLogEntries] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  // Bulk extend
  const [selectedSurveys, setSelectedSurveys] = useState([]);
  const [extendModal, setExtendModal]         = useState(false);
  const [newCloseDate, setNewCloseDate]       = useState('');
  const [extending, setExtending]             = useState(false);

  const loadSurveys = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await surveyService.getAll({ ...filters, page, limit: 7, view: 'active' });
      setSurveys(data.data);
      setPagination(data.pagination);
    } catch (err) {
      toast.error('Failed to load surveys.');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { loadSurveys(); }, [loadSurveys]);

  const toggleSurveySelect = (surveyId) =>
    setSelectedSurveys((prev) =>
      prev.includes(surveyId) ? prev.filter((id) => id !== surveyId) : [...prev, surveyId]
    );

  const toggleSelectAll = () => {
    const eligible = surveys.filter((s) => s.survey_status !== 'closed').map((s) => s.survey_id);
    setSelectedSurveys((prev) => prev.length === eligible.length ? [] : eligible);
  };

  const handleBulkExtend = async () => {
    if (!newCloseDate || !selectedSurveys.length) return;
    setExtending(true);
    try {
      const { data } = await api.put('/surveys/bulk-extend', {
        survey_ids: selectedSurveys,
        new_close_date: newCloseDate,
      });
      toast.success(data.message);
      setExtendModal(false);
      setNewCloseDate('');
      setSelectedSurveys([]);
      await loadSurveys();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to extend deadlines.');
    } finally {
      setExtending(false);
    }
  };

  const openDispatchLog = async (survey) => {
    setLogSurvey(survey);
    setLogModal(true);
    setLogLoading(true);
    try {
      const { data } = await api.get(`/surveys/${survey.survey_id}/dispatch-log`);
      setLogEntries(data.data || []);
    } catch {
      toast.error('Failed to load dispatch log.');
    } finally {
      setLogLoading(false);
    }
  };

  const openDispatch = async (survey) => {
    setDispatchSurvey(survey);
    setSelectedProviders([]);
    setDispatchEstFilter('');
    setDispatchModal(true);
    try {
      // Universal surveys target the whole sector; GQ1 surveys spanning
      // several specific establishments target that whole set; everything
      // else targets one establishment — scope the provider list
      // accordingly so we don't show every active provider system-wide.
      const params = survey.is_universal
        ? { status: 'active', sector: survey.survey_sector_type }
        : survey.establishment_ids?.length
          ? { status: 'active', establishment_ids: survey.establishment_ids.join(',') }
          : { status: 'active', establishment_id: survey.establishment_id };
      const { data } = await providerService.getAll(params);

      // Exclude already-dispatched providers
      const { data: surveyData } = await surveyService.getById(survey.survey_id);
      const dispatchedIds = new Set(
        (surveyData.data.dispatches || []).map((d) => d.dp_id)
      );
      const undispatched = data.data.filter((p) => !dispatchedIds.has(p.dp_id));
      setAllProviders(undispatched);
      setProviders(undispatched);
    } catch {
      toast.error('Failed to load data providers.');
    }
  };

  const handleDispatch = async () => {
    if (!selectedProviders.length) return;
    setDispatching(true);
    try {
      const { data } = await surveyService.dispatch(dispatchSurvey.survey_id, selectedProviders);
      const sent = data.data.filter((d) => d.sent).length;
      toast.success(`Survey dispatched! ${sent}/${selectedProviders.length} email(s) sent.`);
      setDispatchModal(false);
      loadSurveys();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Dispatch failed.');
    } finally {
      setDispatching(false);
    }
  };

  const clearDates = () => setFilters((f) => ({ ...f, date_from: '', date_to: '' }));

  const setFilter = (key, val) => {
    setFilters((f) => ({ ...f, [key]: val }));
    setPage(1);
  };

  return (
    <div className="surveys-list">
      <PageHeader
        title="Surveys"
        subtitle="Active and running surveys"
        actions={
          <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
            <Button variant="ghost" size="sm" onClick={() => navigate('/internal/surveys/past')}>
              <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14" style={{marginRight:'4px'}}><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/></svg>
              Past Surveys
            </Button>
            <Button
              variant="primary"
              onClick={() => navigate('/internal/surveys/create')}
              icon={<svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" /></svg>}
            >
              New Survey
            </Button>
          </div>
        }
      />

      {/* ── Filters ── */}
      <Card className="surveys-list__filters">
        <div className="filters-row">
          <div className="surveys-filter__dates">
            <label className="surveys-filter__dates-label">Date</label>
            <div className="surveys-filter__dates-row">
              <span className="surveys-filter__dates-field">
                <span className="surveys-filter__dates-prefix">From</span>
                <input type="date" value={filters.date_from} max={filters.date_to || undefined}
                  onChange={(e) => setFilter('date_from', e.target.value)} />
              </span>
              <span className="surveys-filter__dates-sep">|</span>
              <span className="surveys-filter__dates-field">
                <span className="surveys-filter__dates-prefix">To</span>
                <input type="date" value={filters.date_to} min={filters.date_from || undefined}
                  onChange={(e) => setFilter('date_to', e.target.value)} />
              </span>
              {(filters.date_from || filters.date_to) && (
                <button className="surveys-filter__clear-dates" onClick={clearDates}>✕</button>
              )}
            </div>
          </div>
          <Select className="surveys-filter__select" value={filters.status} onChange={(e) => setFilter('status', e.target.value)} label="Status">
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s ? (STATUS_LABELS[s] || s.replace('_', ' ').replace(/^\w/, (c) => c.toUpperCase())) : 'All statuses'}</option>
            ))}
          </Select>
          <Select className="surveys-filter__select" value={filters.survey_type} onChange={(e) => setFilter('survey_type', e.target.value)} label="Type">
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>{t ? t.charAt(0).toUpperCase() + t.slice(1) : 'All types'}</option>
            ))}
          </Select>
          <Select className="surveys-filter__select" value={filters.sector} onChange={(e) => setFilter('sector', e.target.value)} label="Sector">
            {SECTOR_OPTIONS.map((s) => (
              <option key={s} value={s}>{s ? s.replace('_', ' & ').replace(/^\w/, (c) => c.toUpperCase()) : 'All sectors'}</option>
            ))}
          </Select>
          <Select className="surveys-filter__select" value={filters.form_code} onChange={(e) => setFilter('form_code', e.target.value)} label="Form">
            {FORM_OPTIONS.map((f) => (
              <option key={f} value={f}>{f ? FORM_LABELS[f] : 'All forms'}</option>
            ))}
          </Select>
        </div>
      </Card>

      {/* ── Table ── */}
      {loading ? (
        <div className="surveys-list__loading"><Spinner size="lg" /></div>
      ) : surveys.length === 0 ? (
        <EmptyState
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>}
          title="No surveys found"
          description="Adjust your filters or create a new survey."
          action={<Button variant="primary" onClick={() => navigate('/internal/surveys/create')}>Create Survey</Button>}
        />
      ) : (
        <Card padding={false}>
          <div className="surveys-table">
            {/* Bulk extend toolbar */}
            {selectedSurveys.length > 0 && (
              <div className="surveys-bulk-toolbar">
                <span className="surveys-bulk-toolbar__count">{selectedSurveys.length} survey{selectedSurveys.length !== 1 ? 's' : ''} selected</span>
                <button className="surveys-bulk-toolbar__btn" onClick={() => setExtendModal(true)}>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h4a1 1 0 100-2H6z" clipRule="evenodd"/>
                  </svg>
                  Extend Deadline
                </button>
                <button className="surveys-bulk-toolbar__clear" onClick={() => setSelectedSurveys([])}>✕ Clear</button>
              </div>
            )}

            <div className="surveys-table__head">
              <span className="surveys-table__head--check">
                <input type="checkbox"
                  ref={(el) => {
                    if (el) {
                      const eligible = surveys.filter(s => s.survey_status !== 'closed').length;
                      el.indeterminate = selectedSurveys.length > 0 && selectedSurveys.length < eligible;
                    }
                  }}
                  checked={selectedSurveys.length > 0 && selectedSurveys.length === surveys.filter(s => s.survey_status !== 'closed').length}
                  onChange={toggleSelectAll}
                />
              </span>
              <span>Survey</span>
              <span>Data Source</span>
              <span>Period</span>
              <span>Dates</span>
              <span>Status / Dispatched</span>
              <span>Actions</span>
            </div>
            {surveys.map((s) => (
              <div key={s.survey_id} className="surveys-table__row">
                <div className="surveys-table__col surveys-table__col--check">
                  {s.survey_status !== 'closed' && (
                    <input type="checkbox"
                      checked={selectedSurveys.includes(s.survey_id)}
                      onChange={() => toggleSurveySelect(s.survey_id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </div>
                <div className="surveys-table__col">
                  <span className="surveys-table__code">{s.survey_form_code}</span>
                  {s.is_universal && (
                    <span className="surveys-table__universal-badge">🌐 Universal</span>
                  )}
                  {s.deadline_extended && (
                    <span className="surveys-table__extended-badge">⏱ Deadline Extended</span>
                  )}
                  <span className="surveys-table__title">{s.survey_title}</span>
                  <span className="surveys-table__creator">by {s.created_by_name}</span>
                </div>
                <div className="surveys-table__col">
                  {s.is_universal
                    ? <span className="surveys-table__all-badge">All</span>
                    : <span className="surveys-table__est">{s.establishment_name || '—'}</span>
                  }
                </div>
                <div className="surveys-table__col">
                  <PeriodBadges periods={s.period_names?.length ? s.period_names : [s.quarter_name || s.month_name]} />
                </div>
                <div className="surveys-table__col surveys-table__col--dates">
                  <span>
                    <strong>Open:</strong> {new Date(s.survey_open_date).toLocaleDateString('en-NA')}
                  </span>
                  <span>
                    <strong>Close:</strong> {new Date(s.survey_close_date).toLocaleDateString('en-NA')}
                  </span>

                </div>
                <div className="surveys-table__col surveys-table__col--meta">
                  <div className="surveys-table__meta-pill">
                    <StatusBadge status={s.survey_status} />
                    <span className="surveys-table__meta-divider" />
                    {statusTime(s) && (
                      <span className="surveys-table__meta-ago">{statusTime(s)}</span>
                    )}
                    {statusTime(s) && <span className="surveys-table__meta-divider" />}
                    <span className="surveys-table__meta-count">{s.dispatched_count ?? 0}</span>
                  </div>
                </div>
                <div className="surveys-table__col surveys-table__col--actions">
                  {/* Icon action buttons */}
                  <div className="sl-actions">
                    {/* View — always visible */}
                    <button className="sl-action sl-action--view" title="View survey"
                      onClick={() => navigate(`/internal/surveys/${s.survey_id}`)}>
                      <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/></svg>
                      <span>View</span>
                    </button>

                    {/* Emails — when dispatched */}
                    {s.dispatched_count > 0 && (
                      <button className="sl-action sl-action--emails" title="View email log"
                        onClick={() => openDispatchLog(s)}>
                        <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/></svg>
                        <span>Emails</span>
                      </button>
                    )}

                    {/* Dispatch — draft or sent */}
                    {['draft', 'sent'].includes(s.survey_status) && (
                      <button className="sl-action sl-action--dispatch" title="Dispatch survey"
                        onClick={() => openDispatch(s)}>
                        <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/></svg>
                        <span>Dispatch</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="surveys-list__pagination">
              <Button variant="secondary" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</Button>
              <span>Page {page} of {pagination.pages}</span>
              <Button variant="secondary" size="sm" disabled={page === pagination.pages} onClick={() => setPage(p => p + 1)}>Next →</Button>
            </div>
          )}
        </Card>
      )}

      {/* ── Bulk Extend Deadline Modal ── */}
      <Modal
        open={extendModal}
        onClose={() => { setExtendModal(false); setNewCloseDate(''); }}
        title="Extend Survey Deadline"
      >
        <div className="extend-modal">
          <p className="extend-modal__desc">
            Extending deadline for <strong>{selectedSurveys.length} survey{selectedSurveys.length !== 1 ? 's' : ''}</strong>.
          </p>
          <p className="extend-modal__note">
            Pending surveys will be automatically reopened. Only surveys where the new date is after the current close date will be updated.
          </p>
          <div className="extend-modal__field">
            <label>New Close Date</label>
            <input
              type="date"
              value={newCloseDate}
              min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
              onChange={(e) => setNewCloseDate(e.target.value)}
            />
          </div>
          <div className="extend-modal__actions">
            <Button variant="secondary" onClick={() => { setExtendModal(false); setNewCloseDate(''); }}>
              Cancel
            </Button>
            <Button variant="primary" loading={extending} disabled={!newCloseDate} onClick={handleBulkExtend}>
              {extending ? 'Extending…' : `Extend ${selectedSurveys.length} Survey${selectedSurveys.length !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Dispatch Log Modal ── */}
      <Modal
        open={logModal}
        onClose={() => setLogModal(false)}
        title={`Email Dispatch Log — ${logSurvey?.survey_form_code}`}
        size="md"
      >
        <div className="dispatch-log">
          <p className="dispatch-log__subtitle">{logSurvey?.survey_title}</p>
          {logLoading ? (
            <div style={{ textAlign: 'center', padding: '32px' }}><Spinner /></div>
          ) : logEntries.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '24px', color: 'var(--gray-400)' }}>
              No emails have been dispatched for this survey yet.
            </p>
          ) : (
            <div className="dispatch-log__list">
              {/* Summary stats */}
              {/* Stats */}
              <div className="dispatch-log__stats">
                <div className="dispatch-log__stat dispatch-log__stat--sent">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                  <div>
                    <span className="dispatch-log__stat-num">{logEntries.filter(e => e.email_sent).length}</span>
                    <span className="dispatch-log__stat-label">Sent</span>
                  </div>
                </div>
                <div className="dispatch-log__stat dispatch-log__stat--failed">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-9a1 1 0 012 0v4a1 1 0 01-2 0V9zm1 7a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/></svg>
                  <div>
                    <span className="dispatch-log__stat-num">{logEntries.filter(e => !e.email_sent).length}</span>
                    <span className="dispatch-log__stat-label">Failed</span>
                  </div>
                </div>
              </div>

              {/* Email rows */}
              <div className="dispatch-log__list">
                {logEntries.map((entry) => (
                  <div key={entry.dispatch_id} className="dispatch-log__row">
                    <div className="dispatch-log__row-left">
                      <div className={`dispatch-log__avatar ${!entry.email_sent ? 'dispatch-log__avatar--failed' : ''}`}>
                        {entry.dp_name?.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()}
                      </div>
                      <div className="dispatch-log__info">
                        <span className="dispatch-log__name">{entry.dp_name}</span>
                        <span className="dispatch-log__email">{entry.dp_email}</span>
                        <span className="dispatch-log__date">
                          Sent: {new Date(entry.dispatched_at).toLocaleString('en-NA')}
                        </span>
                      </div>
                    </div>
                    {entry.email_sent ? (
                      <span className="dispatch-log__badge dispatch-log__badge--sent">
                        <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12"><path fillRule="evenodd" d="M13.707 4.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L6 10.586l6.293-6.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                        Email sent
                      </span>
                    ) : (
                      <span className="dispatch-log__badge dispatch-log__badge--failed">
                        <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12"><path fillRule="evenodd" d="M8 1a7 7 0 100 14A7 7 0 008 1zM7 5a1 1 0 012 0v3a1 1 0 01-2 0V5zm1 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/></svg>
                        Failed
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* ── Dispatch Modal ── */}
      <Modal
        open={dispatchModal}
        onClose={() => setDispatchModal(false)}
        title={`Dispatch Survey — ${dispatchSurvey?.survey_form_code}`}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDispatchModal(false)}>Cancel</Button>
            <Button
              variant="primary"
              loading={dispatching}
              disabled={!selectedProviders.length}
              onClick={handleDispatch}
            >
              Send to {selectedProviders.length} Provider{selectedProviders.length !== 1 ? 's' : ''}
            </Button>
          </>
        }
      >
        <div className="dispatch-modal">
          <div className="dispatch-modal__survey-info">
            <p><strong>{dispatchSurvey?.survey_title}</strong></p>
            {dispatchSurvey?.is_universal
              ? <span className="dispatch-modal__universal-badge">🌐 Universal Survey — {dispatchSurvey?.survey_sector_type === 'manufacturing' ? 'Manufacturing' : 'Wholesale & Retail'} Sector</span>
              : <p>{dispatchSurvey?.establishment_name}</p>
            }
          </div>

          {/* Filters row */}
          <div className="dispatch-modal__filters">
            {/* Establishment filter — for universal surveys */}
            {dispatchSurvey?.is_universal && (
              <select className="dispatch-modal__est-filter"
                value={dispatchEstFilter}
                onChange={(e) => {
                  const val = e.target.value;
                  setDispatchEstFilter(val);
                  setProviders(val
                    ? allProviders.filter((p) =>
                        (p.establishments || []).some((est) => est.establishment_id === val) ||
                        p.establishment_id === val
                      )
                    : allProviders
                  );
                }}
              >
                <option value="">All data sources</option>
                {[...new Map(
                  allProviders.flatMap((p) =>
                    (p.establishments && p.establishments.length > 0)
                      ? p.establishments
                      : p.establishment_name
                        ? [{ establishment_id: p.establishment_id, establishment_name: p.establishment_name }]
                        : []
                  ).map((e) => [e.establishment_id, e])
                ).values()].map((e) => (
                  <option key={e.establishment_id} value={e.establishment_id}>{e.establishment_name}</option>
                ))}
              </select>
            )}

            {/* Select all */}
            {providers.length > 0 && (
              <label className="dispatch-modal__select-all">
                <input type="checkbox"
                  checked={selectedProviders.length === providers.length && providers.length > 0}
                  onChange={() => setSelectedProviders(
                    selectedProviders.length === providers.length ? [] : providers.map((p) => p.dp_id)
                  )}
                />
                Select all ({providers.length})
              </label>
            )}
          </div>

          <p className="dispatch-modal__label">
            Select respondent to notify by email:
            {providers.length > 0 && <span className="dispatch-modal__count"> {providers.length} available</span>}
          </p>

          {providers.length === 0 ? (
            <div className="dispatch-modal__empty">
              {dispatchEstFilter
                ? 'No undispatched respondents for this data source.'
                : 'All active data respondents have already been dispatched.'
              }
              <Button variant="secondary" size="sm" onClick={() => { setDispatchModal(false); navigate('/internal/providers'); }}>
                Add Respondent
              </Button>
            </div>
          ) : (
            <div className="dispatch-modal__list">
              {providers.map((p) => (
                <label
                  key={p.dp_id}
                  className={`dispatch-provider ${selectedProviders.includes(p.dp_id) ? 'dispatch-provider--selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedProviders.includes(p.dp_id)}
                    onChange={() => setSelectedProviders((prev) =>
                      prev.includes(p.dp_id) ? prev.filter((x) => x !== p.dp_id) : [...prev, p.dp_id]
                    )}
                  />
                  <div className="dispatch-provider__avatar">
                    {p.dp_firstname?.[0]}{p.dp_lastname?.[0]}
                  </div>
                  <div className="dispatch-provider__info">
                    <span className="dispatch-provider__name">{p.dp_firstname} {p.dp_lastname}</span>
                    <span className="dispatch-provider__email">{p.dp_email}</span>
                    {/* Show establishments for universal surveys */}
                    {dispatchSurvey?.is_universal && (
                      <div className="dispatch-provider__ests">
                        {(p.establishments && p.establishments.length > 0)
                          ? p.establishments.map((e) => (
                              <span key={e.establishment_id} className="dispatch-provider__est-tag">
                                {e.establishment_name}
                              </span>
                            ))
                          : p.establishment_name
                            ? <span className="dispatch-provider__est-tag">{p.establishment_name}</span>
                            : null
                        }
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default SurveysList;